/**
 * Single source of truth for how a workflow run's status is presented in the web
 * UI. Previously four surfaces (WorkflowRunCard, WorkflowHistoryTable,
 * WorkflowExecution StatusBadge, StatusIcon) each mapped status → colors
 * independently, which drifted: `cancelled` showed a green/success badge,
 * `failed` used `bg-destructive` in one place and `bg-error` in another, and
 * `pending`/`running` were indistinguishable in the detail view. Consolidating
 * here fixes those and keeps the taxonomy in one tested place.
 *
 * Status taxonomy (packages/workflows/src/schemas/workflow-run.ts):
 *   pending | running | paused | failed | cancelled | completed
 */

export interface RunStatusMeta {
  /** Human label. */
  label: string;
  /** Tailwind classes for a pill badge (bg + text + border). */
  badgeClass: string;
  /** Tailwind classes for the status dot. */
  dotClass: string;
}

const META: Record<string, RunStatusMeta> = {
  running: {
    label: 'Running',
    badgeClass: 'bg-primary/10 text-primary border-primary/20',
    dotClass: 'bg-primary animate-pulse',
  },
  pending: {
    label: 'Pending',
    badgeClass: 'bg-surface-elevated text-text-secondary border-border/60',
    dotClass: 'bg-text-tertiary',
  },
  paused: {
    label: 'Paused',
    badgeClass: 'bg-warning/10 text-warning border-warning/20',
    dotClass: 'bg-warning animate-pulse',
  },
  failed: {
    label: 'Failed',
    badgeClass: 'bg-error/10 text-error border-error/20',
    dotClass: 'bg-error',
  },
  cancelled: {
    label: 'Cancelled',
    badgeClass: 'bg-surface-elevated text-text-secondary border-border/60',
    dotClass: 'bg-text-tertiary',
  },
  completed: {
    label: 'Completed',
    badgeClass: 'bg-success/10 text-success border-success/20',
    dotClass: 'bg-success',
  },
};

const FALLBACK: RunStatusMeta = {
  label: 'Unknown',
  badgeClass: 'bg-surface-elevated text-text-secondary border-border/60',
  dotClass: 'bg-text-tertiary',
};

/** Presentation metadata for a run status (unknown statuses get a neutral fallback). */
export function runStatusMeta(status: string | null | undefined): RunStatusMeta {
  if (!status) return FALLBACK;
  return META[status] ?? { ...FALLBACK, label: status };
}
